import Collapsible from './Collapsible';
import { extend, isEmpty, debounce, isObject, isInteger, forOwn, isFunction, isUndefined } from 'lodash-es';

const lineBreakRegEX = /(?:\r\n|\r|\n)/g;
const htmlRegEx = /<[a-z][\s\S]*>/i;

export default class Table {

	/**
	 * Generates a table based on the options given
	 * can be used within the modal and as a standalone feature
	 *
	 * @param options ({})
	 */
	constructor (options) {
		this.columns = [];
		this.rows = [];
		this.rowAttributes = [];
		this.filterTarget = null;
		this.filterMinLength = 3;
		this.autoAppend = true;
		this.empty_message = 'No results found';
		this.table_selector = null;
		this.collapsible = {};
		this.showMoreCount = 0;

		extend(this, options);

		this.currentRows = [];

		if (!this.table_selector) {
			this.createTable();
		}

		if (this.table_selector) {
			this.mapNodeTable();
		}

		if (!isEmpty(this.collapsible)) {
			this.getCollapsibleTable();
		}
	}

	/**
	 * The table may already be on the DOM, so we can map out the appropriate nodes
	 * to the table constructor
	 */
	mapNodeTable () {
		this.table = this.table_selector;
		this.tbody = this.table.querySelector('tbody');
		this.currentRows = this.table.querySelectorAll('tbody > tr');
	}

	/**
	 * Creates a new table
	 */
	createTable () {
		this.table = document.createElement('table');
		this.tbody = document.createElement('tbody');

		if (this.filterTarget && isObject(this.filterTarget)) {
			this.table.setAttribute('filter-target', this.filterTarget.target);

			this.tableFilter = document.createElement('div');
			this.tableFilter.classList.add('modal-filter');
			this.tableFilter.className = 'modal-filter';

			this.filterInput = document.createElement('input');
			this.filterInput.className = 'filter-input';

			this.filterInput.placeholder = this.filterTarget.placeholder;
			this.tableFilter.appendChild(this.filterInput);
			this.filterInput.addEventListener('keyup', debounce(this.filterHandler.bind(this, this.filterInput), 500));
		}

		this.renderHeaders();

		this.table.appendChild(this.tbody);

		this.renderTableContent(true);
	}

	/**
	 * Replaces the data that has any breaks in them with the expected space,
	 * otherwise the content within the cell looks weird
	 *
	 * @param data The cell data
	 *
	 * @returns {string|object}
	 */
	content (data) {
		let dataValue = data;

		if (data === null) {
			return '';
		}

		if (!isObject(data) && !htmlRegEx.test(data)) {
			dataValue = (!isInteger(data))
				? data.replace(lineBreakRegEX, '<br />')
				: data;
		}

		return dataValue;
	}

	/**
	 * Assigns data attributes to a given element
	 *
	 * @param ele The given ele
	 * @param attributes An array of attributes
	 */
	setAttributes (ele, attributes) {
		if (attributes === undefined) {
			return;
		}

		forOwn(attributes, (value, key) => {
			let attributeData = (isObject(value)) ? JSON.stringify(value) : value;
			ele.setAttribute(key, attributeData);
		});
	}

	/**
	 * Sets and binds the action on a cell
	 *
	 * @param action The action to set (if object, is the params)
	 * @param key The key of the action
	 * @param td The cell to bind the action to
	 */
	setCellAction (action, key, td) {
		let params = [],
			callbackAction = action;

		if (isObject(action)) {
			params = action;
			callbackAction = key;
		}

		let callback = (this[callbackAction] || undefined);

		if (isUndefined(callback) || !isFunction(callback)){
			throw new Error('call back is undefined');
		}

		td.appendChild(this.tdAnchor);
		this.tdAnchor.addEventListener('click', callback.bind(td, ...params));
	}

	/**
	 * Sets the cell actions, handling different cell options
	 *
	 * @param dataVal The data values for the cell
	 * @param td The cell
	 */
	setCellActions (dataVal, td) {
		if (isFunction(dataVal.actions)) {
			dataVal.actions = dataVal.actions();
		} else if (isObject(this.cellData)) {
			this.tdAnchor = this.cellData;
		} else {
			this.tdAnchor = document.createElement('a');
			this.tdAnchor.innerHTML = this.cellData;

			if (!isUndefined(dataVal.classes)) {
				this.setClasses(this.tdAnchor, dataVal);
			} else {
				this.tdAnchor.classList.add('mega-modal-selector');
			}
		}

		forOwn(dataVal.actions, (action, key) => {
			this.setCellAction(action, key, td);
		});
	}

	/**
	 * Sets the content for a given cell (can have actionable cells)
	 *
	 * @param dataVal The data values of the cell
	 * @param td The cell
	 */
	setContent (dataVal, td) {
		this.cellData = this.content(dataVal.content);

		if (!isUndefined(dataVal.actions)) {
			this.setCellActions(dataVal, td);
		} else if (isObject(this.cellData)) {
			td.appendChild(this.cellData);
		} else if (dataVal.textOnly) {
			td.appendChild(document.createTextNode(this.cellData));
		} else {
			td.innerHTML = this.cellData;
		}
	}

	/**
	 * Renders the show more if needed
	 *
	 * @param renderedCount The count of rows already rendered
	 * @param rows All the rows that can be rendered
	 */
	renderShowMore (renderedCount, rows) {
		if (this.showMore && this.table.contains(this.showMore)) {
			this.table.removeChild(this.showMore);
		}

		if (renderedCount < rows.length) {
			this.showMore = document.createElement('A');
			let moreCount = ((this.showMoreCount + renderedCount) > rows.length)
				? (rows.length - renderedCount)
				: this.showMoreCount;
			let showMoreText = document.createTextNode(`Show ${moreCount.toString()} More`);

			this.showMore.classList.add('show-more');

			this.showMore.addEventListener('click', () => {
				this.showMore.parentElement.removeChild(this.showMore);
				this.renderRows(rows, moreCount, renderedCount);
			});

			this.showMore.appendChild(showMoreText);
			this.table.appendChild(this.showMore);
		}
	}

	/**
	 * Renders the rows (with show more abilitiy for speedyness)
	 *
	 * @param rows The rows
	 * @param length The amount of rows (length to slice from rows)
	 * @param appendFrom The starting point to slice from
	 */
	renderRows (rows, length, appendFrom = 0) {
		if (!appendFrom) {
			this.removeTableContent(true);
		}

		let sliceTo = (appendFrom + length);
		let reducedRows = rows.slice(appendFrom, sliceTo);

		for (let i = 0; i < reducedRows.length; ++i){
			this.tbody.appendChild(reducedRows[i]);
		}

		this.renderShowMore(this.tbody.childElementCount, rows);
	}

	/**
	 * Applies a filter to the table if filterTarget is assigned then
	 * this will apply the event listener to the related input (currently supported in the modal side only)
	 *
	 * @param input The filter input
	 */
	filterHandler (input) {
		let searchString = input.value.toLowerCase();
		let searchWords = searchString.split(' ');

		if (searchString.length === 0) {
			this.renderRows(this.currentRows, this.showMoreCount);
		} else {
			let filtersRows = this.currentRows.filter((row) => {
				let foundWords = 0;
				let rowText = row.textContent.toLowerCase();

				searchWords.forEach((word) => {
					if (~rowText.indexOf(word)){
						++foundWords;
					}
				});

				return (foundWords === searchWords.length);
			});

			this.renderRows(filtersRows, this.showMoreCount);
		}
	}

	/**
	 * An array of classes are added to the td element if applicable
	 *
	 * @param ele The target element to add classes to
	 * @param dataVal Object of data for a given row (contains other property/values)
	 */
	setClasses (ele, dataVal) {
		if (!isUndefined(dataVal.classes) && (!isEmpty(dataVal.classes))) {
			dataVal.classes.forEach((className) => {
				ele.classList.add(className);
			});
		}
	}

	/**
	 * Assigns a colspan to a td
	 *
	 * @param td The td element
	 * @param span The total columns to span
	 */
	assignColspan (td, span) {
		td.setAttribute('colspan', span);
	}

	/**
	 * Renders header content to the table
	 */
	renderHeaders () {
		if (!isEmpty(this.columns) && this.thead === undefined) {
			this.thead = document.createElement('thead');
			this.headerRow = document.createElement('tr');

			forOwn(this.columns, (value) => {
				this.th = document.createElement('th');
				this.th.appendChild(document.createTextNode(value));
				this.headerRow.appendChild(this.th);
			});

			this.thead.appendChild(this.headerRow);
			this.table.appendChild(this.thead);
		}
	}

	/**
	 * Empty row handler
	 *
	 * @returns {HTMLElement} The empty row element
	 */
	emptyRow () {
		let row = document.createElement('tr');
		let td = document.createElement('td');
		td.appendChild(document.createTextNode(this.empty_message));
		this.assignColspan(td, `${this.columns.length}`);
		row.appendChild(td);

		return row;
	}

	/**
	 * Gets the row to render (tr, tds and content)
	 *
	 * @param val
	 * @param key
	 *
	 * @returns {HTMLElement} The row element with content
	 */
	getRow (val, key) {
		let row = document.createElement('tr');
		this.setAttributes(row, this.rowAttributes[key]);

		forOwn(val, (dataVal) => {
			let cell = (dataVal.headerCell)
				? document.createElement('th')
				: document.createElement('td');

			this.setAttributes(cell, dataVal.dataAttributes);
			this.setAttributes(cell, dataVal.dataTooltip);

			this.setClasses(cell, dataVal);

			if (dataVal.noWrap) {
				cell.style['white-space'] = 'nowrap';
			}

			this.assignColspan(cell, dataVal.colSpan);

			this.setContent(dataVal, cell);

			row.appendChild(cell);
		});

		return row;
	}

	/**
	 * Renders table content
	 *
	 * @param showMore Show the show more button
	 */
	renderTableContent (showMore) {
		let row;

		if (isEmpty(this.rows)) {
			row = this.emptyRow();
			this.tbody.appendChild(row);
			this.currentRows.push(row);
		} else {
			forOwn(this.rows, (val, key) => {
				row = this.getRow(val, key);

				this.currentRows.push(row);
			});
		}

		if (!showMore || this.showMoreCount === 0) {
			this.showMoreCount = this.currentRows.length;
		}

		this.renderRows(this.currentRows, this.showMoreCount);
	}

	/**
	 * Remove the table rows. This is currently used with the AJAX handler,
	 * so it can easily remove/append new rows.
	 */
	removeTableContent (onlyContent) {
		if (!isEmpty(this.currentRows) && !onlyContent) {
			this.currentRows.forEach((row) => {
				this.tbody.removeChild(row);
			});

			this.currentRows = [];
		}

		if (onlyContent) {
			this.table.removeChild(this.tbody);
			this.tbody = document.createElement('tbody');
			this.table.appendChild(this.tbody);
		}
	}

	/**
	 * Create collapsible table
	 */
	getCollapsibleTable () {
		this.collapsible = new Collapsible(Object.assign(this.collapsible, { content: this.table }));

		this.collapsibleTable = this.collapsible.collapsible;
	}

	/**
	 * Returns the table to render
	 */
	getTable () {
		if (!isEmpty(this.collapsible)) {
			return this.collapsibleTable;
		} else {
			return this.table;
		}
	}

}
